// src/pages/Cliente/Perfil/index.jsx
import React, { useEffect, useState } from "react";
import { Alert, Form, Input, App, Row, Col, Select } from "antd";
import {
  UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined,
  CalendarOutlined, HomeOutlined, EditOutlined, SaveOutlined,
  CloseOutlined, CheckCircleOutlined, EnvironmentOutlined,
  MedicineBoxOutlined, TeamOutlined, LockOutlined, KeyOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FiBookOpen, FiAward, FiHeart, FiChevronRight } from "react-icons/fi";
import dayjs from "dayjs";
import "dayjs/locale/es";
import Auth from "../../../../service/Auth";
import { COMUNAS_RM } from "../../../../utils/comunas";
import { useNotify, useWithNotify } from "../../../../providers/NotificationProvider";

dayjs.locale("es");

const { TextArea } = Input;
const { Option } = Select;

const COMUNAS_SANTIAGO = COMUNAS_RM;

// Fuera del componente a propósito: definidos adentro, React los volvería a montar en
// cada render y los formularios de la sección perderían el foco.
const InfoRow = ({ icon, label, value, verified, fullWidth }) => (
  <div className={`flex items-start gap-3 p-4 rounded-2xl bg-gray-50 ${fullWidth ? 'sm:col-span-2' : ''}`}>
    <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-white text-primary flex items-center justify-center shadow-sm">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 m-0">{label}</p>
      <p className="flex items-center gap-1.5 text-[15px] font-semibold text-gray-900 m-0 mt-0.5 break-words">
        {value || <span className="font-normal text-gray-400">No especificado</span>}
        {verified && <CheckCircleOutlined className="text-primary" aria-label="Verificado" />}
      </p>
    </div>
  </div>
);

const Seccion = ({ id, icon, titulo, destacado, acciones, children }) => (
  <section id={id} className="rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.05),0_8px_24px_-12px_rgba(0,0,0,.08)] mb-6 scroll-mt-24">
    <header className="flex flex-wrap items-center justify-between gap-3 px-6 pt-6 pb-4">
      <h2 className="flex items-center gap-3 text-xl font-extrabold text-gray-900 m-0">
        <span className="w-10 h-10 rounded-xl bg-blue-50 text-primary flex items-center justify-center text-lg">{icon}</span>
        <span>{titulo} <span className="hl-amarillo">{destacado}</span></span>
      </h2>
      {acciones && <div className="flex gap-2">{acciones}</div>}
    </header>
    <div className="px-6 pb-6">{children}</div>
  </section>
);

const btnPrimario = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold shadow-sm hover:brightness-110 transition-all disabled:opacity-60';
const btnSecundario = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:border-primary hover:text-primary transition-colors';

export default function PerfilCliente() {
  const [loading, setLoading]               = useState(true);
  const [user, setUser]                     = useState(null);
  const [editing, setEditing]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [changingPassword, setChangingPassword]   = useState(false);
  const [savingPassword, setSavingPassword]       = useState(false);
  const [scrollToPassword, setScrollToPassword]   = useState(false);

  const [form]         = Form.useForm();
  const [passwordForm] = Form.useForm();

  const notify     = useNotify();
  const withNotify = useWithNotify();
  const { modal }  = App.useApp();

  useEffect(() => { loadUserData(); }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const userData = await Auth.Me();
      const userFormatted = {
        id:               userData.id,
        username:         userData.username,
        name:             userData.name             || userData.dynamoProfile?.name,
        email:            userData.email,
        emailVerified:    userData.emailVerified,
        phone:            userData.phone            || userData.dynamoProfile?.phone,
        phoneVerified:    userData.phoneVerified,
        rut:              userData.rut              || userData.dynamoProfile?.rut,
        birthdate:        userData.birthdate        || userData.dynamoProfile?.birthdate,
        address:          userData.address          || userData.dynamoProfile?.address,
        commune:          userData.commune          || userData.dynamoProfile?.commune,
        gender:           userData.gender           || userData.dynamoProfile?.gender,
        enfermedadesBases:userData.enfermedadesBases|| userData.dynamoProfile?.enfermedadesBases,
        role:             userData.role,
        isActive:         userData.dynamoProfile?.isActive,
        createdAt:        userData.dynamoProfile?.createdAt,
        lastLogin:        userData.dynamoProfile?.lastLogin,
      };
      setUser(userFormatted);
      form.setFieldsValue({
        name:              userFormatted.name,
        phone:             userFormatted.phone,
        address:           userFormatted.address,
        birthdate:         userFormatted.birthdate ? dayjs(userFormatted.birthdate) : null,
        commune:           userFormatted.commune,
        gender:            userFormatted.gender,
        enfermedadesBases: userFormatted.enfermedadesBases,
      });
    } catch (error) {
      console.error('Error cargando datos:', error);
      notify.error("No se pudieron cargar los datos del usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => setEditing(true);

  const handleCancelEdit = () => {
    setEditing(false);
    form.setFieldsValue({
      name:              user.name,
      phone:             user.phone,
      address:           user.address,
      birthdate:         user.birthdate ? dayjs(user.birthdate) : null,
      commune:           user.commune,
      gender:            user.gender,
      enfermedadesBases: user.enfermedadesBases,
    });
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      const payload = {
        name:             values.name,
        phone_number:     values.phone,
        address:          values.address,
        commune:          values.commune,
        enfermedadesBases:values.enfermedadesBases || 'Ninguna',
      };
      await withNotify(Auth.UpdateProfile(payload), {
        loading: "Actualizando perfil...",
        success: "Perfil actualizado exitosamente",
        error:   "No se pudo actualizar el perfil",
      });
      setUser(prev => ({ ...prev, name: values.name, phone: values.phone, address: values.address, commune: values.commune, enfermedadesBases: values.enfermedadesBases }));
      setEditing(false);
    } catch (error) {
      console.error('Error actualizando perfil:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = () => {
    setChangingPassword(true);
    setScrollToPassword(true);
  };

  useEffect(() => {
    if (scrollToPassword && changingPassword) {
      const el = document.getElementById("changePassword");
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 120;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      setScrollToPassword(false);
    }
  }, [scrollToPassword, changingPassword]);

  const handleCancelChangePassword = () => {
    setChangingPassword(false);
    passwordForm.resetFields();
  };

  const handleSavePassword = async (values) => {
    try {
      setSavingPassword(true);
      await withNotify(Auth.ChangePassword(values.currentPassword, values.newPassword), {
        loading: "Cambiando contraseña...",
        success: "Contraseña actualizada exitosamente",
        error:   "No se pudo cambiar la contraseña",
      });
      setChangingPassword(false);
      passwordForm.resetFields();
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
    } finally {
      setSavingPassword(false);
    }
  };

  const ROLES = { admin: 'Administrador', coordinador: 'Coordinador', jefe_recinto: 'Jefe de recinto', cliente: 'Vecino' };

  const getGenderLabel = (g) => ({ masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' }[g] || g);

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10" aria-busy="true" aria-label="Cargando perfil">
        <div className="rounded-3xl bg-white overflow-hidden animate-pulse mb-6">
          <div className="h-32 bg-gray-200" />
          <div className="p-6 space-y-3">
            <div className="h-6 bg-gray-200 rounded-full w-1/3" />
            <div className="h-3 bg-gray-100 rounded-full w-1/2" />
          </div>
        </div>
        <div className="rounded-3xl bg-white h-72 animate-pulse" />
      </div>
    );
  }
  if (!user) return <div className="max-w-5xl mx-auto px-4 py-10"><Alert message="No se pudieron cargar los datos del usuario" type="error" showIcon /></div>;

  const initials = user.name ? user.name.split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase() : '?';

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">

        {/* ── Portada ────────────────────────────────────────────────────── */}
        <section className="rounded-3xl bg-white border border-gray-100 overflow-hidden mb-6 shadow-[0_1px_3px_rgba(0,0,0,.05),0_8px_24px_-12px_rgba(0,0,0,.08)]">
          <div className="relative h-32 sm:h-36 bg-[#00266b] overflow-hidden">
            <div aria-hidden className="absolute inset-0 opacity-[.12]" style={{
              backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '22px 22px'
            }} />
            <span aria-hidden className="absolute -right-8 -top-14 w-36 h-36 rounded-full bg-secondary" />
            <span aria-hidden className="absolute right-36 top-8 w-3 h-3 rounded-full bg-secondary" />
            <span aria-hidden className="absolute right-28 bottom-6 w-5 h-5 rounded-full bg-primary" />
          </div>

          <div className="px-6 pb-6">
            {/* Solo el avatar se monta sobre la portada; el nombre va debajo, en blanco. */}
            <div className="relative z-10 w-24 h-24 -mt-12">
              <div className="w-24 h-24 rounded-full bg-primary text-white text-3xl font-extrabold flex items-center justify-center ring-[6px] ring-white shadow-[0_10px_24px_-8px_rgba(0,38,107,.5)]">
                {initials}
              </div>
              <span aria-hidden className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-secondary ring-4 ring-white" />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-3">
              <div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight m-0">{user.name || 'Usuario'}</h1>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="px-3 py-1 rounded-full bg-secondary/40 text-[#5c4300] text-xs font-bold">
                      {ROLES[user.role] || user.role}
                    </span>
                    {user.emailVerified && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-primary text-xs font-bold">
                        <CheckCircleOutlined /> Email verificado
                      </span>
                    )}
                    {user.isActive && (
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold">Cuenta activa</span>
                    )}
                  </div>
                </div>
              </div>
              {!editing && !changingPassword && (
                <div className="flex flex-wrap gap-2 pb-1">
                  <button type="button" onClick={handleChangePassword} className={btnSecundario}>
                    <KeyOutlined /> Contraseña
                  </button>
                  <button type="button" onClick={handleEdit} className={btnPrimario}>
                    <EditOutlined /> Editar perfil
                  </button>
                </div>
              )}
            </div>
            {(user.createdAt || user.lastLogin) && (
              <p className="text-sm text-gray-500 mt-4 mb-0">
                {user.createdAt && <>Miembro desde {dayjs(user.createdAt).format('MMMM [de] YYYY')}</>}
                {user.lastLogin && <> · Último acceso: {dayjs(user.lastLogin).format('DD/MM/YYYY [a las] HH:mm')}</>}
              </p>
            )}
          </div>
        </section>

        {/* ── Accesos rápidos ────────────────────────────────────────────── */}
        <nav className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" aria-label="Accesos de mi cuenta">
          {[
            { to: '/mis-talleres', icon: <FiBookOpen size={18} />, titulo: 'Mis talleres', texto: 'Inscripciones y QR de acceso' },
            { to: '/mis-matriculas', icon: <FiAward size={18} />, titulo: 'Mis matrículas', texto: 'Estado y vencimiento' },
            { to: '/familiares', icon: <FiHeart size={18} />, titulo: 'Mis cargas', texto: 'Familiares a tu cargo' },
          ].map(a => (
            <Link key={a.to} to={a.to}
              className="group flex items-center gap-3 p-4 rounded-2xl bg-white border border-gray-100 hover:border-secondary hover:bg-[#fffbea] transition-colors">
              <span className="w-11 h-11 flex-shrink-0 rounded-xl bg-blue-50 text-primary flex items-center justify-center group-hover:bg-secondary group-hover:text-[#00266b] transition-colors">
                {a.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-gray-900">{a.titulo}</span>
                <span className="block text-xs text-gray-500 truncate">{a.texto}</span>
              </span>
              <FiChevronRight size={16} className="text-gray-300 group-hover:text-[#00266b] transition-colors" />
            </Link>
          ))}
        </nav>

        {/* ── Información Personal ───────────────────────────────────────── */}
        <Seccion
          icon={<UserOutlined />}
          titulo="Información"
          destacado="personal"
          acciones={editing ? (<>
            <button type="button" onClick={handleCancelEdit} className={btnSecundario}>
              <CloseOutlined /> Cancelar
            </button>
            <button type="button" onClick={() => form.submit()} disabled={saving} className={btnPrimario}>
              {saving ? <LoadingOutlined /> : <SaveOutlined />} Guardar
            </button>
          </>) : null}
        >
          {!editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow icon={<UserOutlined />}         label="Nombre completo"       value={user.name} />
              <InfoRow icon={<IdcardOutlined />}        label="RUT"                   value={user.rut} />
              <InfoRow icon={<MailOutlined />}          label="Email"                 value={user.email} verified={user.emailVerified} />
              <InfoRow icon={<PhoneOutlined />}         label="Teléfono"              value={user.phone} />
              <InfoRow icon={<CalendarOutlined />}      label="Fecha de nacimiento"   value={user.birthdate ? dayjs(user.birthdate).format('DD/MM/YYYY') : null} />
              <InfoRow icon={<TeamOutlined />}          label="Género"                value={user.gender ? getGenderLabel(user.gender) : null} />
              <InfoRow icon={<HomeOutlined />}          label="Dirección"             value={user.address} />
              <InfoRow icon={<EnvironmentOutlined />}   label="Comuna"                value={user.commune} />
              <InfoRow icon={<MedicineBoxOutlined />}   label="Condiciones médicas"   value={user.enfermedadesBases} fullWidth />
            </div>
          ) : (
            <Form form={form} layout="vertical" onFinish={handleSave} validateTrigger="onChange">
              <div className="flex items-start gap-3 p-4 mb-5 rounded-2xl bg-blue-50 text-sm text-[#00266b]">
                <IdcardOutlined className="mt-0.5" />
                <span>El RUT y el email no pueden modificarse. Para cambiarlos contacta con soporte.</span>
              </div>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="name" label="Nombre completo"
                    rules={[{ required: true, message: 'Requerido' }, { pattern: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/, message: 'Solo letras y espacios' }, { min: 3, message: 'Mínimo 3 caracteres' }]}
                    hasFeedback validateTrigger="onChange"
                  >
                    <Input prefix={<UserOutlined />} placeholder="Tu nombre completo" size="large" maxLength={100} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="phone" label="Teléfono"
                    rules={[{ required: true, message: 'Requerido' }, { pattern: /^\+56[9]\d{8}$/, message: 'Formato: +56912345678' }]}
                    hasFeedback validateTrigger="onChange" tooltip="Formato: +56912345678"
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="+56 9 1234 5678" size="large" maxLength={12} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="address" label="Dirección"
                    rules={[{ required: true, message: 'Requerida' }, { pattern: /^(?=.*[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ])[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ\s,.#°-]+$/, message: 'Ingrese calle y número' }, { min: 5, message: 'Mínimo 5 caracteres' }]}
                    hasFeedback validateTrigger="onChange"
                  >
                    <Input prefix={<HomeOutlined />} placeholder="Calle y número" size="large" maxLength={200} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="commune" label="Comuna" rules={[{ required: true, message: 'Requerida' }]} hasFeedback>
                    <Select placeholder="Selecciona tu comuna" size="large" showSearch allowClear optionFilterProp="children">
                      {COMUNAS_SANTIAGO.map(c => <Option key={c} value={c}>{c}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="enfermedadesBases" label="Condiciones médicas"
                    rules={[{ max: 500, message: 'Máximo 500 caracteres' }]}
                    hasFeedback validateTrigger="onChange"
                  >
                    <TextArea rows={3} placeholder="Describe condiciones relevantes para actividades físicas..." maxLength={500} showCount />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )}
        </Seccion>

        {/* ── Cambiar Contraseña ─────────────────────────────────────────── */}
        {changingPassword && (
          <Seccion id="changePassword" icon={<LockOutlined />} titulo="Cambiar" destacado="contraseña">
            <div className="flex items-start gap-3 p-4 mb-5 rounded-2xl bg-[#fffbea] border border-[#fde68a] text-sm text-[#5c4300]">
              <KeyOutlined className="mt-0.5" />
              <span>Mínimo 8 caracteres, con una mayúscula, una minúscula, un número y un carácter especial.</span>
            </div>
            <Form form={passwordForm} layout="vertical" onFinish={handleSavePassword}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="currentPassword" label="Contraseña actual" rules={[{ required: true, message: 'Requerida' }]}>
                    <Input.Password prefix={<LockOutlined />} placeholder="••••••••" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="newPassword" label="Nueva contraseña"
                    rules={[{ required: true, message: 'Requerida' }, { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_+=\-\[\]\\;'`~]).{8,}$/, message: 'Contraseña insegura' }]}
                  >
                    <Input.Password prefix={<KeyOutlined />} placeholder="••••••••" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="confirmPassword" label="Confirmar contraseña"
                    dependencies={['newPassword']}
                    rules={[{ required: true, message: 'Requerida' }, ({ getFieldValue }) => ({ validator(_, v) { return !v || getFieldValue('newPassword') === v ? Promise.resolve() : Promise.reject('Las contraseñas no coinciden'); } })]}
                  >
                    <Input.Password prefix={<KeyOutlined />} placeholder="••••••••" size="large" />
                  </Form.Item>
                </Col>
              </Row>
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={savingPassword} className={btnPrimario}>
                  {savingPassword ? <LoadingOutlined /> : <SaveOutlined />} Cambiar contraseña
                </button>
                <button type="button" onClick={handleCancelChangePassword} className={btnSecundario}>
                  <CloseOutlined /> Cancelar
                </button>
              </div>
            </Form>
          </Seccion>
        )}
      </div>
    </div>
  );
}
